import {
  keepPreviousData,
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { apiFetch } from './client';
import { queryKeys } from './queryKeys';
import { responseFetch } from './responseFetch';
import type { AdminIntegrationConnection, Job, Run, User } from '../types';

type AdminJobFilters = {
  userId?: string;
  state?: string;
};

type AdminRunFilters = {
  userId?: string;
  jobId?: string;
  state?: string;
};

export function useAdminUsers() {
  return useQuery({
    queryKey: queryKeys.adminUsers(),
    queryFn: () => apiFetch<User[]>('/api/v1/admin/users'),
    placeholderData: keepPreviousData,
  });
}

export function useAdminUser(userId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.adminUser(userId),
    queryFn: userId
      ? () => apiFetch<User>(`/api/v1/admin/users/${encodeURIComponent(userId)}`)
      : skipToken,
  });
}

export function useAdminJobs(filters: AdminJobFilters = {}) {
  return useQuery({
    queryKey: queryKeys.adminJobs(filters),
    queryFn: () => apiFetch<Job[]>(adminPath('/api/v1/admin/jobs', filters)),
    placeholderData: keepPreviousData,
  });
}

export function useAdminUserJobs(userId: string | undefined, state?: string) {
  return useQuery({
    queryKey: queryKeys.adminUserJobs(userId, state),
    queryFn: userId
      ? () =>
          apiFetch<Job[]>(
            adminPath(`/api/v1/admin/users/${encodeURIComponent(userId)}/jobs`, { state }),
          )
      : skipToken,
    placeholderData: keepPreviousData,
  });
}

export function useAdminJob(jobId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.adminJob(jobId),
    queryFn: jobId
      ? () => apiFetch<Job>(`/api/v1/admin/jobs/${encodeURIComponent(jobId)}`)
      : skipToken,
  });
}

export function useAdminRuns(filters: AdminRunFilters = {}, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.adminRuns(filters),
    queryFn: () => apiFetch<Run[]>(adminPath('/api/v1/admin/runs', filters)),
    enabled: options.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

export function useAdminUserRuns(
  userId: string | undefined,
  filters: Omit<AdminRunFilters, 'userId'> = {},
) {
  return useQuery({
    queryKey: queryKeys.adminUserRuns(userId, filters),
    queryFn: userId
      ? () =>
          apiFetch<Run[]>(
            adminPath(`/api/v1/admin/users/${encodeURIComponent(userId)}/runs`, filters),
          )
      : skipToken,
    placeholderData: keepPreviousData,
  });
}

export function useAdminRun(runId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.adminRun(runId),
    queryFn: runId
      ? () => apiFetch<Run>(`/api/v1/admin/runs/${encodeURIComponent(runId)}`)
      : skipToken,
  });
}

export function useAdminUserIntegrations(userId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.adminUserIntegrations(userId),
    queryFn: userId
      ? () =>
          apiFetch<AdminIntegrationConnection[]>(
            `/api/v1/admin/users/${encodeURIComponent(userId)}/integrations`,
          )
      : skipToken,
    placeholderData: keepPreviousData,
  });
}

export function useDeleteAdminUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      await responseFetch(`/api/v1/admin/users/${encodeURIComponent(userId)}`, {
        method: 'DELETE',
      });
    },
    onSuccess: async (_data, userId) => {
      queryClient.removeQueries({ queryKey: queryKeys.adminUser(userId) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.adminPrefix() });
    },
  });
}

export function useDeleteAdminUserIntegration(userId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (integrationType: string) => {
      // Guard instead of `userId!`: without it an undefined userId would DELETE
      // `/users/undefined/integrations/...`. Reject cleanly so onSuccess never runs.
      if (!userId) throw new Error('useDeleteAdminUserIntegration: userId is required');
      await responseFetch(
        `/api/v1/admin/users/${encodeURIComponent(userId)}/integrations/${encodeURIComponent(
          integrationType,
        )}`,
        { method: 'DELETE' },
      );
    },
    onSuccess: async () => {
      if (userId) {
        await queryClient.invalidateQueries({
          queryKey: queryKeys.adminUserIntegrations(userId),
        });
      }
    },
  });
}

function adminPath(base: string, filters: Record<string, string | undefined>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value) params.set(key, value);
  }
  const query = params.toString();
  return query ? `${base}?${query}` : base;
}
